// App do cliente (app.licita.dev): a empresa vê só o que é dela. Dinheiro em centavos (Minor).
import { z } from 'zod';
import { ClienteInput, Contato } from './clientes';
import { UFS } from './editais';
import { FitAnalise } from './fits';
import { ETAPAS, Pipeline } from './pipeline';

export const INTERESSES = ['participar', 'nao', 'ajuda'] as const;
export const INTERESSE_NOMES: Record<(typeof INTERESSES)[number], string> = {
  participar: 'Vou participar',
  nao: 'Não me interessa',
  ajuda: 'Quero ajuda da equipe',
};

/** O que o cliente pode editar no próprio perfil (CNPJ, porte e ativo são da equipe). */
export const EmpresaInput = ClienteInput.pick({
  nomeFantasia: true,
  ufs: true,
  aceitaPresencial: true,
  cnaes: true,
  descricao: true,
  atestados: true,
  certificacoes: true,
  faturamentoAnualMinor: true,
  valorMinimoMinor: true,
  valorMaximoMinor: true,
  evitar: true,
  contatos: true,
});
export type EmpresaInput = z.input<typeof EmpresaInput>;

export const Empresa = EmpresaInput.omit({ contatos: true }).extend({
  id: z.string().uuid(),
  razaoSocial: z.string(),
  cnpj: z.string(),
  porte: z.string(),
  ufs: z.array(z.string()),
  cnaes: z.array(z.string()),
  contatos: z.array(Contato),
  updatedAt: z.string(),
});

export const AppEdital = z.object({
  fitId: z.string().uuid(),
  editalId: z.string().uuid(),
  numeroControlePncp: z.string(),
  objeto: z.string(),
  modalidadeNome: z.string(),
  orgao: z.string(),
  uf: z.string().nullable(),
  municipio: z.string().nullable(),
  valorEstimadoMinor: z.number().nullable(),
  publicadoEm: z.string().nullable(),
  encerramentoPropostasEm: z.string().nullable(),
  linkPncp: z.string(),
  linkSistemaOrigem: z.string().nullable(),
  nota: z.number().nullable(),
  analise: FitAnalise.nullable(),
  /** Resumo da ficha (o que o órgão quer), sem o resto da ficha. */
  resumoEdital: z.string().nullable(),
  entregaveis: z.array(z.string()),
  tecnologias: z.array(z.string()),
  categoria: z.string().nullable(),
  riscos: z.array(z.string()),
  aprovadoEm: z.string(),
  alertaEnviadoEm: z.string().nullable(),
  interesse: z.object({ interesse: z.enum(INTERESSES), observacao: z.string().nullable(), em: z.string() }).nullable(),
  pipeline: Pipeline,
});
export type AppEdital = z.infer<typeof AppEdital>;

export const APP_ORDENS = ['prazo', 'nota', 'publicacao', 'valor'] as const;
export const AppEditaisQuery = z.object({
  filtro: z.enum(['abertos', 'interesse', 'todos']).default('abertos'),
  etapa: z.enum(ETAPAS).optional(),
  /** Texto: objeto, unidade (busca em português sem acento) ou nome do órgão. */
  q: z.string().trim().max(200).optional(),
  uf: z.enum(UFS).optional(),
  modalidade: z.coerce.number().int().min(1).max(13).optional(),
  valorMinMinor: z.coerce.number().int().nonnegative().optional(),
  valorMaxMinor: z.coerce.number().int().nonnegative().optional(),
  /** Só com encerramento das propostas em até N dias. */
  prazoAteDias: z.coerce.number().int().min(1).max(365).optional(),
  ordem: z.enum(APP_ORDENS).default('prazo'),
});
export const AppEditaisResponse = z.object({ itens: z.array(AppEdital) });

export const InteresseInput = z.object({
  interesse: z.enum(INTERESSES),
  observacao: z.string().trim().max(2000).nullable().default(null),
});

/** Equipe dá acesso ao app a uma pessoa da empresa: cria a conta (papel cliente) e manda o link mágico. */
export const AcessoInput = z.object({
  nome: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().email().max(200),
});
export const Acesso = z.object({ userId: z.string(), nome: z.string(), email: z.string(), criadoEm: z.string() });
export const AcessosResponse = z.object({ itens: z.array(Acesso) });

/** "Isso não me interessa": o termo entra em `evitar` no perfil e pesa nos próximos fits. */
export const EvitarInput = z.object({ termo: z.string().trim().min(2).max(80) });
export const EvitarResponse = z.object({ evitar: z.array(z.string()) });

/** Empresas às quais a pessoa tem acesso; a ativa vai no header `x-empresa` (id). */
export const EmpresaResumo = z.object({ id: z.string().uuid(), nome: z.string(), cnpj: z.string() });
export const EmpresasResponse = z.object({ itens: z.array(EmpresaResumo) });

export const NOTIFICACAO_TIPOS = ['comentario', 'etapa', 'edital_aprovado', 'anexo'] as const;
export const Notificacao = z.object({
  id: z.string().uuid(),
  tipo: z.enum(NOTIFICACAO_TIPOS),
  fitId: z.string().uuid().nullable(),
  titulo: z.string(),
  texto: z.string().nullable(),
  lidaEm: z.string().nullable(),
  em: z.string(),
});
export const NotificacoesResponse = z.object({ naoLidas: z.number(), itens: z.array(Notificacao) });
export const LidasInput = z.object({ ids: z.array(z.string().uuid()).max(100).optional() });
